// Line-count audit (2026-05-04): 274 lines.
import { useCallback } from 'react';
import * as agentConversationManager from '@/services/editor/AgentConversationManager';
import { getLastStreamingDiff } from '@/services/editor/CanvasActionApplicator';
import { buildPipelineSteps, type PipelineStep } from '@/components/ai/StreamingProgressIndicator';
import { userAgentApi } from '@/services/api/agentApi';
import type { AgentVisualResponse, CanvasAction } from '@/services/api/agentApi';
import type { MultiPageProgress } from '@/components/ai/StreamingProgressIndicator';
import type { PropertyMessage } from '@/components/ai/types';
import type { SSEComplete } from '@/services/editor/AgentStreamingService';
import type { StreamingCallbacks, UseAgentStreamDeps, UseAgentStreamReturn } from './agentMessagingTypes';

type StreamingCompleteResponse = SSEComplete & Partial<AgentVisualResponse>;

export function useAgentStream(deps: UseAgentStreamDeps): UseAgentStreamReturn {
  const {
    agentVisualThreadIdRef,
    streamingActionCountRef,
    pendingConfirmationActionsRef,
    setIsLoading,
    setStreamingSteps,
    setStreamingMessage,
    setIsStreamingActive,
    setMultiPageProgress,
    setPendingConfirmation,
    setPendingChangeSummary,
    setProactiveSuggestions,
    project,
    setMessages,
    persistMessage,
    generateMessageId,
  } = deps;

  const buildStreamingCallbacks = useCallback(
    (loadingMsg: PropertyMessage, inputText: string): StreamingCallbacks => ({
      onThread: (threadId: string) => {
        agentVisualThreadIdRef.current = threadId;
        if (project?.id) {
          agentConversationManager.persistThreadId(project.id, 'visual-editor', threadId);
        }
      },
      onStepStart: (data: { step: string; agentName: string }) => {
        setStreamingSteps((previous) => previous.map((step) =>
          step.name === data.step ? { ...step, status: 'active' as const } : step
        ));
        setStreamingMessage(`Running ${data.agentName} agent...`);
      },
      onActions: (data: { actions?: CanvasAction[]; requiresConfirmation?: boolean }) => {
        if (!data.requiresConfirmation) {
          streamingActionCountRef.current += data.actions?.length || 0;
          return;
        }

        pendingConfirmationActionsRef.current.push(...(data.actions || []));
        setStreamingMessage('Waiting for your approval...');
      },
      onStepComplete: (data: { step: string; duration_ms: number; actionsCount: number }) => {
        setStreamingSteps((previous) => previous.map((step) =>
          step.name === data.step
            ? {
                ...step,
                status: 'complete' as const,
                duration_ms: data.duration_ms,
                actionsCount: data.actionsCount,
              }
            : step
        ));
      },
      onMemoryCreated: (data: { memoryType: string; text: string }) => {
        const icon = data.memoryType === 'fact' ? '\u25C6' : '\u25C7';
        setStreamingMessage(`${icon} ${data.text}`);
      },
      onVerificationResult: (data: {
        step: string;
        passed: boolean;
        issues: { message?: string; [key: string]: unknown }[];
        summary: string;
      }) => {
        setStreamingSteps((previous) => previous.map((step) =>
          step.name.toLowerCase() === data.step.toLowerCase()
            ? {
                ...step,
                verification: {
                  passed: data.passed,
                  issueCount: data.issues.length,
                  summary: data.summary,
                },
              }
            : step
        ));

        if (!data.passed) {
          setStreamingMessage(`\u26A0 ${data.summary}`);
        }
      },
      onMessage: (data: { text: string }) => {
        setStreamingMessage(data.text);
      },
      onComplete: (data: SSEComplete) => {
        const actionCount = streamingActionCountRef.current;
        const getShortSummary = (message: string) => {
          const colonIndex = message.indexOf(': ');
          if (colonIndex > 0 && colonIndex < 100) {
            return message.slice(0, colonIndex);
          }

          const periodIndex = message.indexOf('. ');
          if (periodIndex > 0 && periodIndex < 100) {
            return message.slice(0, periodIndex);
          }

          return message.slice(0, 80).trimEnd();
        };

        const summary = data.message ? getShortSummary(data.message) : null;
        const responseContent = summary
          ? actionCount > 0
            ? `${summary} — ${actionCount} change${actionCount !== 1 ? 's' : ''} applied.`
            : `${summary}.`
          : actionCount > 0
            ? `Applied ${actionCount} change${actionCount !== 1 ? 's' : ''}.`
            : 'Done.';

        const responseMessage: PropertyMessage = {
          id: generateMessageId(),
          content: responseContent,
          role: 'assistant',
          timestamp: new Date(),
          applied: actionCount > 0,
          suggestions: data.followUpSuggestions,
        };

        setMessages((previous) => [
          ...previous.filter((message) => message.id !== loadingMsg.id),
          responseMessage,
        ]);
        persistMessage(responseMessage);

        const completionResponse: StreamingCompleteResponse = data;
        if (completionResponse.requiresConfirmation) {
          const confirmationResponse: AgentVisualResponse = {
            ...completionResponse,
            actions: pendingConfirmationActionsRef.current,
          };
          setPendingConfirmation(confirmationResponse);
          setPendingChangeSummary(getLastStreamingDiff());
        }

        const proactiveSuggestions = completionResponse.proactiveSuggestions;
        if (proactiveSuggestions && proactiveSuggestions.length > 0) {
          setProactiveSuggestions(proactiveSuggestions);
        }

        userAgentApi.notifyActivity?.({
          activityType: 'visual-edit',
          summary: `User performed visual editing: "${inputText.slice(0, 200)}"`,
          metadata: { actionsCount: actionCount },
        }).catch(() => {});
      },
      onError: (data: { message: string }) => {
        const errorMessage: PropertyMessage = {
          id: generateMessageId(),
          content: `Error: ${data.message}`,
          role: 'assistant',
          timestamp: new Date(),
        };

        setMessages((previous) => [
          ...previous.filter((message) => message.id !== loadingMsg.id),
          errorMessage,
        ]);
        persistMessage(errorMessage);
      },
      onMultiPagePlan: (data: { totalPages: number; pages: Array<{ pageId?: string; pageName: string }> }) => {
        const progress: MultiPageProgress = {
          totalPages: data.totalPages,
          currentPageIndex: 0,
          currentPageName: data.pages[0]?.pageName || 'Page 1',
          pages: data.pages.map((page) => ({
            pageId: page.pageId || `page-${page.pageName}`,
            pageName: page.pageName,
            status: 'pending' as const,
          })),
        };
        setMultiPageProgress(progress);
      },
      onPageStart: (data: { pageIndex: number; pageName: string }) => {
        setMultiPageProgress((previous) => previous ? {
          ...previous,
          currentPageIndex: data.pageIndex,
          currentPageName: data.pageName,
          pages: previous.pages.map((page, index) =>
            index === data.pageIndex
              ? { ...page, status: 'active' as const }
              : index < data.pageIndex
                ? { ...page, status: 'complete' as const }
                : page
          ),
        } : null);
        setStreamingSteps((previous) => previous.map((step) => ({
          ...step,
          status: 'pending' as const,
          duration_ms: undefined,
          actionsCount: undefined,
        } satisfies PipelineStep)));
      },
      onPageComplete: (data: { pageIndex: number }) => {
        setMultiPageProgress((previous) => previous ? {
          ...previous,
          pages: previous.pages.map((page, index) =>
            index === data.pageIndex ? { ...page, status: 'complete' as const } : page
          ),
        } : null);
      },
      onStreamingStart: () => {
        setIsStreamingActive(true);
        const hasDataKeywords = /\b(data|schema|sensor|metrics|kpi|revenue|sales)\b/i.test(inputText);
        const isSimple = /\b(change|update|modify|adjust|make)\b/i.test(inputText)
          && !/\b(build|create|generate|make)\b.*\b(page|dashboard|layout)\b/i.test(inputText);
        const pipelineType = isSimple
          ? 'single-agent' as const
          : hasDataKeywords
            ? 'data-first' as const
            : 'template-first' as const;
        setStreamingSteps(buildPipelineSteps(pipelineType));
        setPendingChangeSummary(null);
      },
      onStreamingEnd: (_response: AgentVisualResponse | null) => {
        setIsStreamingActive(false);
        setMultiPageProgress(null);
        setTimeout(() => {
          setStreamingSteps([]);
          setStreamingMessage(null);
        }, 2000);
      },
    }),
    [
      agentVisualThreadIdRef,
      streamingActionCountRef,
      pendingConfirmationActionsRef,
      setStreamingSteps,
      setStreamingMessage,
      setIsStreamingActive,
      setMultiPageProgress,
      setPendingConfirmation,
      setPendingChangeSummary,
      setProactiveSuggestions,
      project,
      setMessages,
      persistMessage,
      generateMessageId,
    ]
  );

  const handleStopGeneration = useCallback(() => {
    agentConversationManager.abortStreaming();
    setIsLoading(false);
    setIsStreamingActive(false);
    setStreamingSteps([]);
    setStreamingMessage(null);
    setMultiPageProgress(null);
  }, [
    setIsLoading,
    setIsStreamingActive,
    setMultiPageProgress,
    setStreamingMessage,
    setStreamingSteps,
  ]);

  return {
    buildStreamingCallbacks,
    handleStopGeneration,
  };
}
